import { useEffect, useMemo, useRef, useState } from 'react';

import { useBridgeApi } from '@/api/BridgeApi.ts';
import { BrandSpace } from '@/spaces/brand/BrandSpace.tsx';
import { CampaignSpace } from '@/spaces/campaigns/CampaignSpace.tsx';
import { Chat } from '@/spaces/chat/Chat.tsx';
import { ContentSpace } from '@/spaces/content/ContentSpace.tsx';
import { PeopleSpace } from '@/spaces/people/PeopleSpace.tsx';
import { SettingsSpace } from '@/spaces/settings/SettingsSpace.tsx';
import { SPACE_FADE_MS } from '@/utils/animationConstants';
import { cn } from '@/utils/cn';

import type { Space } from '../../../bridge/types/state.ts';

export const Spaces = () => {
	const bridgeApi = useBridgeApi();
	const currentSpace = bridgeApi.user_state?.current_space ?? null;

	const [displayedSpace, setDisplayedSpace] = useState<Space | null>(
		currentSpace,
	);
	const [isExpanded, setIsExpanded] = useState(false);
	const isExpandedRef = useRef(false);
	const isExitingRef = useRef(false);

	useEffect(() => {
		isExpandedRef.current = isExpanded;
	}, [isExpanded]);

	useEffect(() => {
		if (currentSpace) {
			isExitingRef.current = false;
			setDisplayedSpace(currentSpace);

			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					setIsExpanded(true);
				});
			});
		} else if (displayedSpace) {
			if (isExpandedRef.current) {
				isExitingRef.current = true;
				setIsExpanded(false);
			} else {
				setDisplayedSpace(null);
				isExitingRef.current = false;
			}
		}
	}, [currentSpace, displayedSpace]);

	const handleTransitionEnd = (e: React.TransitionEvent) => {
		if (e.propertyName !== 'opacity') return;

		if (isExitingRef.current && !isExpanded) {
			setDisplayedSpace(null);
			isExitingRef.current = false;
		}
	};

	const spaceContent: Record<string, React.ReactNode> = useMemo(
		() => ({
			content: <ContentSpace />,
			campaigns: <CampaignSpace />,
			brand: <BrandSpace />,
			contacts: <PeopleSpace />,
			audiences: <PeopleSpace />,
			settings: <SettingsSpace />,
		}),
		[],
	);

	const content = displayedSpace ? spaceContent[displayedSpace.type] : null;

	if (!displayedSpace || !content) {
		return (
			<div className="flex min-w-0 flex-1 overflow-visible">
				<Chat />
			</div>
		);
	}

	return (
		<div className="flex flex-1 overflow-visible">
			<div
				className={cn(
					'min-w-0 flex-1 overflow-visible transition-opacity ease-out',
					isExpanded ? 'opacity-100' : 'opacity-0',
				)}
				style={{
					transitionDuration: `${SPACE_FADE_MS}ms`,
				}}
				onTransitionEnd={handleTransitionEnd}
			>
				{content}
			</div>
		</div>
	);
};
